import { diffArchitectures, diffPatterns, diffTimelineAdjacent, diffTimelineMoments, } from '@finos/calm-models/diff';
import { initLogger } from '../../logger.js';
function architectureLevelChangeCounts(diff) {
    const adrItems = diff.adrDiffItems ?? [];
    return {
        adrs: {
            added: adrItems.filter((item) => item.changeType === 'added').length,
            removed: adrItems.filter((item) => item.changeType === 'removed').length,
            unchanged: adrItems.filter((item) => item.changeType === 'unchanged').length,
        },
        controls: {
            added: Object.keys(diff.controlItemsAdded ?? {}).length,
            removed: Object.keys(diff.controlItemsRemoved ?? {}).length,
            modified: Object.keys(diff.controlItemsModified ?? {}).length,
        },
        metadata: {
            added: diff.metadataObjectsAdded?.length ?? 0,
            removed: diff.metadataObjectsRemoved?.length ?? 0,
            modified: diff.metadataObjectsModified?.length ?? 0,
        },
    };
}
export function hasChanges(diff) {
    const counts = architectureLevelChangeCounts(diff);
    return (diff.nodesAdded.length > 0 ||
        diff.nodesRemoved.length > 0 ||
        diff.nodesModified.length > 0 ||
        diff.nodesRenamed.length > 0 ||
        diff.edgesAdded.length > 0 ||
        diff.edgesRemoved.length > 0 ||
        diff.edgesModified.length > 0 ||
        diff.edgesRenamed.length > 0 ||
        counts.adrs.added > 0 ||
        counts.adrs.removed > 0 ||
        counts.controls.added > 0 ||
        counts.controls.removed > 0 ||
        counts.controls.modified > 0 ||
        counts.metadata.added > 0 ||
        counts.metadata.removed > 0 ||
        counts.metadata.modified > 0 ||
        (diff.invalidItems?.nodes.length ?? 0) > 0 ||
        (diff.invalidItems?.relationships.length ?? 0) > 0 ||
        (diff.undiffableItems?.nodes.length ?? 0) > 0 ||
        (diff.undiffableItems?.relationships.length ?? 0) > 0);
}
/**
 * Label for a node/relationship in the summary view. Falls back to a content
 * hint for pattern items that have no pinned `unique-id`, so they don't render
 * as `undefined`.
 */
function nodeLabel(node) {
    const item = node;
    if (typeof item['unique-id'] === 'string')
        return item['unique-id'];
    const detail = [item['node-type'], item['name']].filter((v) => typeof v === 'string').join(' ');
    return detail ? `(unpinned ${detail})` : '(unpinned node)';
}
function edgeLabel(edge) {
    const item = edge;
    return typeof item['unique-id'] === 'string' ? item['unique-id'] : '(unpinned relationship)';
}
export function formatDiff(diff, format, documentType = 'architecture') {
    if (format === 'json') {
        return JSON.stringify(diff, null, 2);
    }
    const invalidNodes = diff.invalidItems?.nodes.length ?? 0;
    const invalidEdges = diff.invalidItems?.relationships.length ?? 0;
    const undiffableNodes = diff.undiffableItems?.nodes.length ?? 0;
    const undiffableEdges = diff.undiffableItems?.relationships.length ?? 0;
    const counts = architectureLevelChangeCounts(diff);
    const title = `CALM ${documentType} diff`;
    const lines = [
        title,
        '-'.repeat(title.length),
        `Nodes:         +${diff.nodesAdded.length}  -${diff.nodesRemoved.length}  ~${diff.nodesModified.length}  ↔${diff.nodesRenamed.length}  =${diff.nodesSame.length}`,
        `Relationships: +${diff.edgesAdded.length}  -${diff.edgesRemoved.length}  ~${diff.edgesModified.length}  ↔${diff.edgesRenamed.length}  =${diff.edgesSame.length}`,
    ];
    if (counts.adrs.added + counts.adrs.removed > 0) {
        lines.push(`ADRs:          +${counts.adrs.added}  -${counts.adrs.removed}  =${counts.adrs.unchanged}`);
    }
    if (counts.controls.added + counts.controls.removed + counts.controls.modified > 0) {
        lines.push(`Controls:      +${counts.controls.added}  -${counts.controls.removed}  ~${counts.controls.modified}`);
    }
    if (counts.metadata.added + counts.metadata.removed + counts.metadata.modified > 0) {
        lines.push(`Metadata:      +${counts.metadata.added}  -${counts.metadata.removed}  ~${counts.metadata.modified}`);
    }
    if (invalidNodes + invalidEdges > 0) {
        lines.push(`Invalid items: ${invalidNodes} node(s) + ${invalidEdges} relationship(s) skipped (missing unique-id)`);
    }
    if (undiffableNodes + undiffableEdges > 0) {
        lines.push(`Undiffable items: ${undiffableNodes} node(s) + ${undiffableEdges} relationship(s) (no constrained unique-id to diff by)`);
    }
    lines.push('');
    const list = (label, ids) => {
        if (ids.length === 0)
            return;
        lines.push(label);
        for (const id of ids)
            lines.push(`  - ${id}`);
        lines.push('');
    };
    list('Nodes added:', diff.nodesAdded.map(nodeLabel));
    list('Nodes removed:', diff.nodesRemoved.map(nodeLabel));
    list('Nodes modified:', diff.nodesModified.map((n) => nodeLabel(n.original)));
    list('Nodes renamed:', diff.nodesRenamed.map((r) => `${r.oldId} -> ${r.newId}`));
    list('Relationships added:', diff.edgesAdded.map(edgeLabel));
    list('Relationships removed:', diff.edgesRemoved.map(edgeLabel));
    list('Relationships modified:', diff.edgesModified.map((e) => edgeLabel(e.original)));
    list('Relationships renamed:', diff.edgesRenamed.map((r) => `${r.oldId} -> ${r.newId}`));
    const adrItems = diff.adrDiffItems ?? [];
    list('ADRs added:', adrItems.filter((item) => item.changeType === 'added').map((item) => item.content));
    list('ADRs removed:', adrItems.filter((item) => item.changeType === 'removed').map((item) => item.content));
    list('Controls added:', Object.keys(diff.controlItemsAdded ?? {}));
    list('Controls removed:', Object.keys(diff.controlItemsRemoved ?? {}));
    list('Controls modified:', Object.keys(diff.controlItemsModified ?? {}));
    return lines.join('\n');
}
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function looksLikePattern(doc) {
    const hasNodeOrRelProps = (schema) => {
        const props = isObject(schema['properties']) ? schema['properties'] : undefined;
        return !!props && (isObject(props['nodes']) || isObject(props['relationships']));
    };
    if (hasNodeOrRelProps(doc))
        return true;
    return Array.isArray(doc['allOf'])
        && doc['allOf'].some((sub) => isObject(sub) && hasNodeOrRelProps(sub));
}
/**
 * Classifies a document as an architecture instance (top-level
 * `nodes`/`relationships` arrays) or a pattern (a JSON Schema describing those
 * arrays under `properties`/`allOf`). Returns `null` when the input clearly
 * matches neither shape, leaving the decision to the caller.
 */
export function tryDetectDocumentType(doc) {
    if (Array.isArray(doc['nodes']) || Array.isArray(doc['relationships'])) {
        return 'architecture';
    }
    if (looksLikePattern(doc)) {
        return 'pattern';
    }
    return null;
}
/**
 * Like {@link tryDetectDocumentType} but throws when the input matches neither
 * shape, so malformed input is surfaced rather than silently diffed to an empty
 * result.
 */
export function detectDocumentType(doc) {
    const detected = tryDetectDocumentType(doc);
    if (detected) {
        return detected;
    }
    throw new Error('Could not determine the CALM document type: expected an architecture ' +
        '(top-level nodes/relationships arrays) or a pattern (a JSON Schema ' +
        'describing them). Pass --type architecture|pattern to specify it explicitly.');
}
export function diffDocuments(docA, docB, options = {}) {
    const logger = initLogger(!!options.verbose, 'calm-diff');
    const format = options.format ?? 'json';
    const [labelA, labelB] = options.labels ?? ['document A', 'document B'];
    let documentType;
    if (options.documentType) {
        documentType = options.documentType;
        for (const [label, doc] of [[labelA, docA], [labelB, docB]]) {
            const detected = tryDetectDocumentType(doc);
            if (detected && detected !== documentType) {
                throw new Error(`--type was set to '${documentType}', but ${label} matches '${detected}'. ` +
                    'Remove --type to auto-detect, or pass inputs of the forced type.');
            }
        }
    }
    else {
        const typeA = detectDocumentType(docA);
        const typeB = detectDocumentType(docB);
        if (typeA !== typeB) {
            throw new Error(`Cannot diff mismatched document types: ${typeA} vs ${typeB}. Both inputs must be the ` +
                'same CALM document type; pass --type to override detection.');
        }
        documentType = typeA;
    }
    const diff = documentType === 'pattern'
        ? diffPatterns(docA, docB)
        : diffArchitectures(docA, docB);
    const invalidNodeCount = diff.invalidItems?.nodes.length ?? 0;
    const invalidEdgeCount = diff.invalidItems?.relationships.length ?? 0;
    if (invalidNodeCount + invalidEdgeCount > 0) {
        logger.warn(`Skipped ${invalidNodeCount} node(s) and ${invalidEdgeCount} relationship(s) ` +
            'because they were missing a unique-id. These items are reported under ' +
            'invalidItems and contribute to hasChanges so --exit-code does not pass on them silently.');
    }
    const undiffableNodeCount = diff.undiffableItems?.nodes.length ?? 0;
    const undiffableEdgeCount = diff.undiffableItems?.relationships.length ?? 0;
    if (undiffableNodeCount + undiffableEdgeCount > 0) {
        logger.warn(`Could not diff ${undiffableNodeCount} node(s) and ${undiffableEdgeCount} relationship(s) ` +
            'because they constrain no comparable content (e.g. an unconstrained unique-id). ' +
            'These items are reported under undiffableItems and contribute to hasChanges so ' +
            '--exit-code does not pass on them silently.');
    }
    const formatted = formatDiff(diff, format, documentType);
    return { diff, formatted, hasChanges: hasChanges(diff) };
}
export async function diffTimeline(timeline, resolver, options = {}) {
    if (options.fromMomentId || options.toMomentId) {
        if (!options.fromMomentId || !options.toMomentId) {
            throw new Error('Both fromMomentId and toMomentId must be supplied to diff a specific pair.');
        }
        const diff = await diffTimelineMoments(timeline, options.fromMomentId, options.toMomentId, resolver);
        return { diffs: [diff] };
    }
    const diffs = await diffTimelineAdjacent(timeline, resolver);
    return { diffs };
}
//# sourceMappingURL=diff-core.js.map